<template>
    <b-form class="create-connector-form" @submit.stop.prevent="onSubmit">
        <div id="type" class="group-wrapper">
            <label>Product</label>
            <div class="buttons-wrapper">
                <b-form-radio
                    v-for="product in products"
                    :key="product"
                    v-model="form.product"
                    :value="product"
                >
                    {{ product }}
                </b-form-radio>
            </div>
        </div>
        <div id="connector-name" class="group-wrapper">
            <label>Connector Name</label>
            <div>
                <b-form-input
                    id="connector-name"
                    v-model="form.name"
                    required
                    @focus="shouldHideError('name')"
                />
                <p>{{ validationErrors.name }}</p>
            </div>
        </div>

        <div id="tenant" class="group-wrapper">
            <label>Tenant</label>
            <multiselect
                v-model="form.tenant"
                :options="tenants"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder=""
                :preselect-first="true"
                label="name"
                track-by="_id"
                :show-labels="false"
                @open="shouldHideError('tenant')"
            />
            <p>{{ validationErrors.tenant }}</p>
        </div>

        <div id="timezone" class="group-wrapper">
            <label>Time Zone</label>
            <div class="timezone-select-wrapper">
                <multiselect
                    v-model="form.timeZone"
                    :options="timeZones"
                    :clear-on-select="false"
                    :preserve-search="true"
                    placeholder=""
                    :preselect-first="true"
                    :show-labels="false"
                    :allow-empty="false"
                    @open="shouldHideError('timeZone')"
                />
                <p>{{ validationErrors.timeZone }}</p>
            </div>
        </div>

        <div class="group-wrapper">
            <label>IP</label>

            <div class="connector-ip-input-wrapper">
                <b-form-input id="connector-ip" v-model="form.ip" @blur="shouldHideError('ip')" />
                <p class="error">{{ validationErrors.ip }}</p>
            </div>
        </div>

        <div id="type" class="group-wrapper">
            <label>Type</label>
            <div class="buttons-wrapper">
                <b-form-radio
                    v-model="form.connectorType"
                    value="VITRO"
                    @change="setDefaultVersion"
                >
                    Vitro
                </b-form-radio>
                <b-form-radio
                    v-model="form.connectorType"
                    :disabled="isOQ"
                    value="GERI"
                    @change="setDefaultVersion"
                >
                    Geri
                </b-form-radio>
                <b-form-radio
                    v-model="form.connectorType"
                    :disabled="isOQ"
                    value="MIRI"
                    @change="setDefaultVersion"
                >
                    Miri
                </b-form-radio>
            </div>
        </div>

        <div v-if="isEQ" id="focals" class="group-wrapper">
            <label>Focals</label>
            <div class="focals-input-wrapper">
                <b-form-input
                    id="focals"
                    v-model="form.focals"
                    :disabled="isNotVitro"
                    :placeholder="focalsPlaceholder"
                    :required="true"
                    @focus="shouldHideError('focals')"
                />
                <p>{{ validationErrors.focals }}</p>
            </div>
        </div>

        <div v-if="isEQ" id="shouldFetchOnlyActiveTreatments" class="group-wrapper">
            <label>Should fetch only active treatments</label>
            <b-form-checkbox
                v-model="form.shouldFetchOnlyActiveTreatments"
                :value="true"
                :unchecked-value="false"
            />
        </div>

        <div class="divider">
            <label>Lab API</label>
        </div>

        <div v-if="isEQ" id="api-version" class="group-wrapper">
            <label>API Version</label>
            <div class="version-select-wrapper">
                <multiselect
                    v-model="form.labApi.version"
                    :options="typeVersions"
                    :clear-on-select="false"
                    :preserve-search="true"
                    placeholder=""
                    :preselect-first="true"
                    :show-labels="false"
                    @open="shouldHideError('version')"
                />
                <p>{{ validationErrors.version }}</p>
            </div>
        </div>

        <div id="url" class="group-wrapper">
            <label>URL</label>
            <div>
                <b-form-input
                    id="url"
                    v-model="form.labApi.url"
                    required
                    @focus="shouldHideError('url')"
                />
                <p>{{ validationErrors.url }}</p>
            </div>
        </div>

        <div id="user-name" class="group-wrapper">
            <label>User Name</label>
            <b-form-input id="userName" v-model="form.labApi.user" required />
        </div>

        <div id="password" class="group-wrapper">
            <label>Password</label>
            <b-form-input id="password" v-model="form.labApi.password" required />
        </div>

        <div id="is-active" class="group-wrapper">
            <label>Is Active</label>
            <b-form-checkbox v-model="form.isActive" :value="true" :unchecked-value="false" />
        </div>

        <div id="submit" class="group-wrapper">
            <b-button type="submit" variant="primary">Submit</b-button>
        </div>
    </b-form>
</template>

<script>
import Multiselect from 'vue-multiselect'
import UIUtils from '../../../../business/utils/UIUtils'
import BackendProvider from '../../../../business/services/BackendProvider'
import { TIMEZONES } from '../../../../business/services/timezones'
import {
    INSTRUMENT_TYPE,
    GERI_API_VERSIONS,
    VITRO_API_VERSIONS,
    MIRI_API_VERSIONS,
    PRODUCTS
} from '../../../../business/const'
import Utils from '../../../../business/utils/Utils'

export default {
    name: 'CreateConnectorForm',
    components: { Multiselect },
    data() {
        return {
            products: [PRODUCTS.EQ, PRODUCTS.OQ],
            form: {
                product: PRODUCTS.EQ,
                name: '',
                tenant: null,
                timeZone: TIMEZONES.GMT,
                ip: '',
                focals: '',
                connectorType: INSTRUMENT_TYPE.VITRO,
                shouldFetchOnlyActiveTreatments: true,
                isActive: false,
                labApi: {
                    url: '',
                    user: '',
                    password: '',
                    version: ''
                }
            },
            tenants: [],
            timeZones: Object.values(TIMEZONES),
            validationErrors: {
                name: '',
                tenant: '',
                timeZone: '',
                ip: '',
                version: '',
                focals: '',
                url: ''
            },
            validationMessages: {
                DUPLICATE_NAME: 'Connector name already exists',
                DUPLICATE_URL: 'URL already exists for this tenant',
                MUST_SELECT: 'Please select a value',
                INVALID_FOCALS: 'Focals must be numbers between -5 and 5',
                NO_RESPONSE: 'No response from lab API URL',
                INVALID_CREDENTIALS: 'Invalid lab API credentials',
                INVALID_IP: 'Please, enter valid IP'
            },
            focalsPlaceholder: 'Comma seperated values -1,0,1'
        }
    },
    computed: {
        isOQ() {
            return this.form.product === PRODUCTS.OQ
        },
        isEQ() {
            return this.form.product === PRODUCTS.EQ
        },
        isNotVitro() {
            return this.form.connectorType !== INSTRUMENT_TYPE.VITRO
        },
        focalsArray() {
            return this.form.focals.split(',').map(focal => Number(focal))
        },
        typeVersions() {
            const { VITRO, GERI, MIRI } = INSTRUMENT_TYPE

            const versionsByVendor = {
                [VITRO]: VITRO_API_VERSIONS,
                [GERI]: GERI_API_VERSIONS,
                [MIRI]: MIRI_API_VERSIONS
            }

            return versionsByVendor[this.form.connectorType]
        }
    },
    mounted() {
        this.loadTenants()
        this.setDefaultVersion()
    },
    methods: {
        setDefaultVersion() {
            const { VITRO, GERI, MIRI } = INSTRUMENT_TYPE

            const defaultVersionByVendor = {
                [VITRO]: VITRO_API_VERSIONS[0],
                [GERI]: GERI_API_VERSIONS[0],
                [MIRI]: MIRI_API_VERSIONS[0]
            }

            this.form.labApi.version = defaultVersionByVendor[this.form.connectorType]

            if (this.isNotVitro) this.clearFocalsInput()
        },
        clearFocalsInput() {
            this.form.focals = ''
        },
        async onSubmit() {
            UIUtils.showLoadingMask()
            this.validateInputFields()

            if (this.isInputValid())
                try {
                    await this.testConnection()
                    const createdConnector = await this.createConnector()
                    UIUtils.toast(`connector id: ${createdConnector._id}`, 'message', 10 * 1000)
                    this.$bvModal.hide('create_connector')
                } catch (e) {
                    this.handleValidationError(e.message)
                }
            UIUtils.hideLoadingMask()
        },
        async testConnection() {
            const { labApi, connectorType, product } = this.form

            const testConnectionDto = {
                labApi
            }
            if (product === PRODUCTS.EQ) {
                testConnectionDto.connectorType = connectorType
            } else {
                testConnectionDto.vendor = connectorType
                delete testConnectionDto.labApi.version
            }

            return await BackendProvider.testConnection(testConnectionDto, product)
        },
        async createConnector() {
            const {
                name,
                tenant,
                timeZone,
                ip,
                connectorType,
                isActive,
                product,
                labApi
            } = this.form

            const createConnectorDto = {
                name,
                tenantId: tenant._id,
                timeZone,
                ip,
                isActive,
                labApi
            }

            if (product === PRODUCTS.EQ) {
                createConnectorDto.connectorType = connectorType
                createConnectorDto.focals = this.focalsArray
                createConnectorDto.shouldFetchOnlyActiveTreatments =
                    this.form.shouldFetchOnlyActiveTreatments
            } else {
                createConnectorDto.vendor = connectorType
            }

            return await BackendProvider.createConnector(createConnectorDto, product)
        },
        validateInputFields() {
            if (!this.form.tenant)
                this.validationErrors.tenant = this.validationMessages.MUST_SELECT

            if (!this.form.timeZone)
                this.validationErrors.timeZone = this.validationMessages.MUST_SELECT

            this.validationErrors.ip = ''
            if (this.form.ip && !this.isValidIp(this.form.ip)) {
                this.validationErrors.ip = this.validationMessages.INVALID_IP
            }

            if (!this.form.labApi.version)
                this.validationErrors.version = this.validationMessages.MUST_SELECT

            const focals = this.focalsArray
            const isFocalsValid = focals.every(
                focal => typeof focal === 'number' && focal <= 5 && focal >= -5
            )
            if (!isFocalsValid)
                this.validationErrors.focals = this.validationMessages.INVALID_FOCALS
        },
        isInputValid() {
            return Object.values(this.validationErrors).every(value =>
                Utils.isNullOrEmptyString(value)
            )
        },
        handleValidationError(message) {
            switch (message) {
                case this.validationMessages.DUPLICATE_NAME:
                    this.validationErrors.name = message
                    break
                case this.validationMessages.DUPLICATE_URL:
                    this.validationErrors.url = message
                    break
                case this.validationMessages.INVALID_CREDENTIALS:
                    UIUtils.toast(
                        this.$translate('lab_api_invalid_credentials'),
                        'danger',
                        10 * 1000
                    )
                    break
                case this.validationMessages.NO_RESPONSE:
                    UIUtils.toast(this.$translate('lab_api_no_response'), 'danger', 10 * 1000)
                    break
                default:
                    UIUtils.toast(message, 'danger', 10 * 1000)
            }
        },
        shouldHideError(id) {
            if (this.validationErrors[id]) this.validationErrors[id] = ''
        },
        async loadTenants() {
            this.tenants = await BackendProvider.getAllTenants()
        },
        isValidIp(ip) {
            // https://medium.com/@stheodorejohn/validating-ipv4-and-ipv6-addresses-with-ease-unveiling-the-power-of-validation-in-javascript-2af04ee065c5#:~:text=To%20validate%20an%20IP%20address,1%20to%204%20hexadecimal%20digits.
            const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/
            const ipv6Regex = /^([\da-f]{1,4}:){7}[\da-f]{1,4}$/i

            if (ipv4Regex.test(ip)) {
                return ip.split('.').every(part => parseInt(part) <= 255)
            }
            if (ipv6Regex.test(ip)) {
                return ip.split(':').every(part => part.length <= 4)
            }
            return false
        }
    }
}
</script>

<style lang="scss" scoped>
.create-connector-form {
    position: relative;
    width: 30rem;

    .divider {
        padding: 0.5rem;
        color: $light;
    }

    .group-wrapper {
        position: relative;
        display: grid;
        grid-template-columns: 40% 60%;
        align-items: center;
        min-height: 4rem;
        padding-inline: 0.5rem;

        label {
            padding-top: 3%;
        }

        p {
            color: $red;
            font-size: 0.9rem;
            margin: 0;
            padding-top: 0.1rem;
        }

        .form-control:disabled {
            background-color: $light-alpha;
        }

        .buttons-wrapper {
            display: flex;
            justify-content: space-around;
        }
    }

    #submit.group-wrapper {
        display: flex;
        justify-content: center;
    }
}
</style>

<!--override vue multiselect component styles-->
<style lang="scss">
#tenant.group-wrapper,
#timezone.group-wrapper,
#api-version.group-wrapper {
    .multiselect {
        z-index: 2;
        font-size: 0.9rem;
        position: relative;

        .multiselect__tags {
            background-color: $dark-2;
            border-radius: 0.3rem;
            border-color: $light;

            .multiselect__single {
                color: $light-2;
                background-color: transparent;
                font-size: 0.9rem;
            }

            .multiselect__placeholder {
                font-size: 0.9rem;
            }
            .multiselect__input {
                background-color: $dark-2;
                color: $light;
                font-size: 0.9rem;
            }
        }

        .multiselect__content-wrapper {
            position: absolute;
            max-height: 10rem;
            background-color: $dark-2;
            border-color: $light;
            border-radius: 0.3rem;

            .multiselect__content {
                background-color: $dark-2;
                color: $light-2;
                max-height: 10rem;

                .multiselect__element {
                    .multiselect__option--highlight {
                        background-color: $blue;
                    }
                    .multiselect__option--selected {
                        background-color: $blue;
                    }
                }
            }
        }
    }

    .multiselect--active {
        position: absolute;
        top: 0.8rem;
        left: 12.1rem;
        width: 17.43rem;
        height: 100%;
    }
}

#tenant.group-wrapper {
    .multiselect {
        z-index: 4;
    }
}

#timezone.group-wrapper {
    .multiselect {
        z-index: 3;
    }
}

#api-version.group-wrapper {
    .multiselect {
        z-index: 2;
    }
}
</style>
